// PublishBook.tsx
// Tela para autores publicarem seus próprios livros no catálogo (CatalogBook)
// Fluxo: preenche metadados + faz upload do PDF → vai para moderação

import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../global-components/src";
import { useMessage } from "../../../global-components/src/index";
import { READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import "./style.css";

interface PublishForm {
  title: string;
  author: string;
  description: string;
  cover_url: string;
  price: string;
  pages: number;
}

const EMPTY_FORM: PublishForm = {
  title: "",
  author: "",
  description: "",
  cover_url: "",
  price: "0",
  pages: 0,
};

export default function PublishBook() {
  const { token, user } = useAuth();
  const { setMessage } = useMessage();
  const navigate = useNavigate();

  const [form, setForm] = useState<PublishForm>(EMPTY_FORM);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof PublishForm | "file", string>>>({});
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isFree, setIsFree] = useState(true);
  const [coverFile, setCoverFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFileSelect = (file: File) => {
    if (file.type !== "application/pdf") {
      setErrors((prev) => ({ ...prev, file: "Somente arquivos PDF são aceitos." }));
      return;
    }
    if (file.size > 51200 * 1024) {
      setErrors((prev) => ({ ...prev, file: "O arquivo deve ter no máximo 50 MB." }));
      return;
    }
    setPdfFile(file);
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setPdfDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  const validate = (): boolean => {
    const errs: typeof errors = {};
    if (!form.title.trim()) errs.title = "O título é obrigatório.";
    if (!form.author.trim()) errs.author = "O autor é obrigatório.";
    if (!form.description.trim()) errs.description = "A descrição é obrigatória.";
    if (!pdfFile) errs.file = "Faça upload do arquivo PDF.";
    if (!isFree && (isNaN(Number(form.price)) || Number(form.price) <= 0)) {
      errs.price = "Informe um preço válido maior que zero.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || loading) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      const data = new FormData();
      data.append("file", pdfFile!);
      data.append("title", form.title);
      data.append("author", form.author);
      data.append("total_pages", String(form.pages));
      data.append("description", form.description);
      data.append("type", "own_work");
      data.append("price", isFree ? "0" : form.price);

      if (coverFile) {
        data.append("cover_file", coverFile);
      } else {
        data.append("cover_url", form.cover_url);
      }

      await axios.post(`${READBOOKS_API_URL}/catalog/upload`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (ev) => {
          if (ev.total) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        },
      });

      setMessage("Livro enviado para aprovação! Avisaremos quando for publicado.", "success");
      navigate("/catalogo");
    } catch (err: any) {
      const msg =
        err.response?.data?.message ?? "Erro ao publicar. Tente novamente.";
      setMessage(msg, "error");

      if (err.response?.data?.errors) {
        const apiErrs = err.response.data.errors as Record<string, string[]>;
        const mapped: typeof errors = {};
        for (const [k, v] of Object.entries(apiErrs)) {
          mapped[k as keyof typeof errors] = v[0];
        }
        setErrors(mapped);
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const pdfSizeMB = pdfFile ? (pdfFile.size / (1024 * 1024)).toFixed(1) : null;

  return (
    <div className="publish-root">
      {/* Decoração de fundo */}
      <div className="publish-bg-decoration" aria-hidden="true">
        <span>§</span><span>§</span><span>§</span>
      </div>

      <div className="publish-container">
        {/* Cabeçalho */}
        <header className="publish-header">
          <div className="publish-header-text">
            <h1>Publicar obra</h1>
            <p>Envie seu livro para o catálogo. Após revisão ele ficará disponível para leitura.</p>
          </div>
        </header>

        <form className="publish-form" onSubmit={handleSubmit} noValidate>
          <div className="publish-grid">

            {/* ── Coluna esquerda: metadados ── */}
            <div className="publish-col-meta">

              <div className="publish-section-label">Informações do livro</div>

              {/* Título */}
              <div className={`publish-field ${errors.title ? "has-error" : ""}`}>
                <label htmlFor="pub-title">Título <span className="req">*</span></label>
                <input
                  id="pub-title"
                  name="title"
                  type="text"
                  placeholder="Nome completo da obra"
                  value={form.title}
                  onChange={handleChange}
                  maxLength={255}
                  autoFocus
                />
                {errors.title && <span className="publish-error">{errors.title}</span>}
              </div>

              {/* Autor */}
              <div className={`publish-field ${errors.author ? "has-error" : ""}`}>
                <label htmlFor="pub-author">Autor <span className="req">*</span></label>
                <input
                  id="pub-author"
                  name="author"
                  type="text"
                  placeholder="Seu nome ou pseudônimo"
                  value={form.author}
                  onChange={handleChange}
                  maxLength={255}
                />
                {errors.author && <span className="publish-error">{errors.author}</span>}
              </div>
             
              {/* Autor */}
              <div className={`publish-field ${errors.pages ? "has-error" : ""}`}>
                <label htmlFor="pub-pages">Número de páginas <span className="req">*</span></label>
                <input
                  id="pub-pages"
                  name="pages"
                  type="text"
                  placeholder="Seu nome ou pseudônimo"
                  value={form.pages}
                  onChange={handleChange}
                  maxLength={255}
                />
                {errors.pages && <span className="publish-error">{errors.pages}</span>}
              </div>

              {/* Descrição */}
              <div className={`publish-field ${errors.description ? "has-error" : ""}`}>
                <label htmlFor="pub-desc">Descrição <span className="req">*</span></label>
                <textarea
                  id="pub-desc"
                  name="description"
                  placeholder="Sinopse, sobre o autor, tema principal…"
                  value={form.description}
                  onChange={handleChange}
                  rows={5}
                />
                {errors.description && <span className="publish-error">{errors.description}</span>}
              </div>

              {/* Capa */}
              <div className="publish-field">
                <label htmlFor="pub-cover">URL da capa <span className="optional">(opcional)</span></label>
                <div className="publish-cover-row">
                  <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setCoverFile(e.target.files[0]);
                        }
                      }}
                    />

                    {coverFile && (
                      <img
                        src={URL.createObjectURL(coverFile)}
                        className="publish-cover-preview"
                    />
                    )}
                </div>
              </div>

              {/* Preço */}
              <div className="publish-field">
                <label>Precificação</label>
                <div className="publish-price-toggle">
                  <button
                    type="button"
                    className={`publish-price-btn ${isFree ? "active" : ""}`}
                    onClick={() => setIsFree(true)}
                  >
                    Gratuito
                  </button>
                  <button
                    type="button"
                    className={`publish-price-btn ${!isFree ? "active" : ""}`}
                    onClick={() => setIsFree(false)}
                  >
                    Pago
                  </button>
                </div>

                {!isFree && (
                  <div className={`publish-price-input ${errors.price ? "has-error" : ""}`}>
                    <span className="publish-currency">R$</span>
                    <input
                      name="price"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0,00"
                      value={form.price}
                      onChange={handleChange}
                    />
                    {errors.price && <span className="publish-error">{errors.price}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* ── Coluna direita: upload PDF ── */}
            <div className="publish-col-upload">
              <div className="publish-section-label">Arquivo PDF</div>

              <div
                className={`publish-dropzone ${pdfDragOver ? "drag-over" : ""} ${pdfFile ? "has-file" : ""} ${errors.file ? "has-error" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setPdfDragOver(true); }}
                onDragLeave={() => setPdfDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                />

                {pdfFile ? (
                  <div className="publish-file-info">
                    <div className="publish-file-icon">📄</div>
                    <div className="publish-file-details">
                      <strong>{pdfFile.name}</strong>
                      <span>{pdfSizeMB} MB</span>
                    </div>
                    <button
                      type="button"
                      className="publish-file-remove"
                      onClick={(e) => { e.stopPropagation(); setPdfFile(null); }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="publish-dropzone-empty">
                    <div className="publish-dropzone-icon">⬆</div>
                    <p>Arraste o PDF aqui ou <span>clique para selecionar</span></p>
                    <small>Máximo 50 MB · somente .pdf</small>
                  </div>
                )}
              </div>

              {errors.file && <span className="publish-error">{errors.file}</span>}

              {/* Barra de progresso de upload */}
              {loading && uploadProgress > 0 && (
                <div className="publish-upload-progress">
                  <div className="publish-upload-bar">
                    <div
                      className="publish-upload-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span>{uploadProgress}% enviado</span>
                </div>
              )}

              {/* Informações sobre o processo */}
              <div className="publish-info-box">
                <h3>📋 Como funciona</h3>
                <ul>
                  <li>Seu livro passará por moderação antes de ser publicado. Podendo levar até 7 dias apra ser analisado</li>
                  <li>Obras gratuitas ficam disponíveis para todos os leitores.</li>
                  <li>Obras pagas requerem compra antes da leitura.</li>
                  <li>Você não pode alterar informações do livro depois de enviar para a submissão.</li>
                  <li>Respeite os direitos autorais — envie somente obras próprias.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Rodapé do form */}
          <div className="publish-footer">
            <button
              type="button"
              className="publish-btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="publish-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="publish-spinner" />
                  {uploadProgress < 100 ? "Enviando…" : "Processando…"}
                </>
              ) : (
                "Publicar obra"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}